import type { IntlShape } from 'react-intl'

export function capitalise(str = '') {
  if (str.length > 0) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
  return str
}
export function capitaliseAll(str = ''): string {
  const wordList = str.split(' ')
  if (wordList.length > 1) {
    const capitalisedWordList = wordList.map((word, index) => {
      if (index === 0 || (word.length > 2 && !capitaliseIgnoreSet.has(word))) {
        return capitalise(word)
      }
      return word
    })

    return capitalisedWordList.join(' ')
  }

  return str
}

export function contains(str: string, search: string) {
  return str.toLowerCase().includes(search.toLowerCase())
}

function isUpperCase(str: string) {
  const upperStr = str.toUpperCase()
  return str === upperStr
}

/**
 * Convert list of tokens to AP Title Case
 */
export const capitaliseIgnoreSet = new Set(['a', 'an', 'and', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on', 'or', 'so', 'the', 'to', 'up', 'yet'])

function toTitle(tokens: Array<string>) {
  return tokens.map((token, idx) => {
    return (idx === 0 || !capitaliseIgnoreSet.has(token)) ? capitalise(token) : token
  }).join(' ')
}

/**
 * Convert snake case string to AP Title Case
 *
 * @remarks
 * Does not support non-alpha chars
 */
export function snakeToTitle(str: string) {
  const tokens = str.split('_')
  return toTitle(tokens)
}

/**
  * Convert camel case string to AP Title Case
  *
  * @remarks
  * Does not support non-alpha chars
  */
export function camelToTitle(str: string) {
  const tokens: Array<string> = []
  let anchor = 0

  for (let i = 0; i < str.length; i++) {
    if (!isUpperCase(str.charAt(i))) { continue }
    let token = str.slice(anchor, i).toLowerCase()
    if (i === 0 || capitaliseIgnoreSet.has(token)) { token = capitalise(token) }
    tokens.push(token)
    anchor = i
  }

  // loop doesn't handle adding the last token - add it explicitly
  tokens.push(str.slice(anchor, str.length))
  return toTitle(tokens)
}

const KILO = 1024

const PREFIXED_BYTES = [
  'Bytes',
  'KB',
  'MB',
  'GB',
  'TB',
  'PB',
  'EB',
  'ZB',
  'YB',
] as const

export function formatBytes(
  bytes: number,
  decimalPoints = 2,
): `${number} ${typeof PREFIXED_BYTES[number]}` {
  if (bytes == 0) return '0 Bytes'

  const i = Math.floor(Math.log(bytes) / Math.log(KILO))

  return `${parseFloat((bytes / Math.pow(KILO, i)).toFixed(decimalPoints))} ${
    PREFIXED_BYTES[i]
  }`
}

/**
 * Formats a list of strings using Intl list semantics.
 * Accepts semantic types (`conjunction` / `disjunction`) and legacy string aliases (`and` / `or`).
 */
export function arrayToStringListFormat(
  array: Array<string>,
  joinType: 'conjunction' | 'disjunction' | 'and' | 'or' = 'or',
  intl: IntlShape,
) {
  const type = joinType === 'and'
    ? 'conjunction'
    : joinType === 'or'
      ? 'disjunction'
      : joinType
  const formatter = new Intl.ListFormat(intl.locale || 'en', { type })
  return formatter.format(array)
}

export function areStringsEqual(str1: string, str2: string): boolean {
  if (!str1 || !str2) return false
  return str1.toLowerCase().trim() === str2.toLowerCase().trim()
}

export function isString(val: unknown): val is string {
  return typeof val === 'string'
}

export function capitaliseFirstLetterOnly<T extends string>(str: T): Capitalize<T> {
  if (str?.length > 0) {
    return (str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()) as Capitalize<T>
  }
  return str as Capitalize<T>
}

export function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Convert a string to PascalCase removing non-alphabetical characters
 * @param str The string to convert
 * @returns The PascalCase string
 */
export function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z]+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
}

/**
 * Safely converts a string to a number, handling edge cases:
 * - Returns undefined for empty string, null, or undefined
 * - Returns undefined for non-numeric values (NaN)
 * - Preserves valid numbers including 0
 */
export function safeNumberConversion(str: string): number | undefined {
  if (str === '') return undefined
  const num = Number(str)
  if (Number.isNaN(num)) return undefined
  return num
}

/**
 * Formats a number of guests into a human-readable string
 * @param totalGuests The total number of guests
 * @returns A formatted string like "Two people" or "One person", or undefined if totalGuests is 0 or falsy
 * @example
 * formatGuestsDisplay(1) // "One person"
 * formatGuestsDisplay(2) // "Two people"
 * formatGuestsDisplay(0) // undefined
 */
export function formatGuestsDisplay(totalGuests: number, intl: IntlShape): string | undefined {
  if (!totalGuests) return undefined

  // Import numToWords inline to avoid circular dependencies
  const { numToWords } = require('./numToWords')

  const guestsWord = numToWords(totalGuests).trim()
  const capitalizedGuests = capitalise(guestsWord)
  return intl.formatMessage(
    {
      id: 'tu/ImICk9j',
      defaultMessage: '{capitalizedGuests} {totalGuests, plural, one {person} other {people}}',
      description: 'Formatted guests text combining number in words with singular or plural guest noun',
    },
    {
      capitalizedGuests,
      totalGuests,
    },
  )
}
